/**
 * Founder Journey — strict step-by-step onboarding for founders.
 *
 * Reads from /api/founder/journey which derives status from the underlying
 * collections (startups, documents, qa_sessions, psycho_reports, agreements).
 * Locked steps render disabled with a tooltip explaining which prior step
 * must be completed first.
 */
import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Lock, CheckCircle2, Circle, Download, Upload, FileText, FileSignature, Brain, Target, Sparkles, Clock, XCircle, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const STEP_ICONS = {
  profile: Target,
  pitch_deck: FileText,
  ai_vetting: Brain,
  psychometric: Circle,
  nda_ready: FileSignature,
  mou_ready: FileSignature,
};

function StatusPill({ status }) {
  const map = {
    completed: { c: 'bg-emerald-50 text-emerald-700 border-emerald-200', l: 'Complete' },
    open:      { c: 'bg-[#FFF8E5] text-[#5E1F8B] border-[#8B3FC5]', l: 'Open' },
    blocked:   { c: 'bg-rose-50 text-rose-700 border-rose-200', l: 'Blocked · retry' },
    locked:    { c: 'bg-slate-50 text-slate-400 border-slate-200', l: 'Locked' },
  };
  const m = map[status] || map.locked;
  return (
    <span className={`text-[9px] uppercase tracking-[0.22em] font-bold px-2 py-1 rounded border ${m.c}`}>
      {m.l}
    </span>
  );
}

/* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the fetch + polling effects. */
export default function FounderJourney() {
  const [j, setJ] = useState(null);
  const [busy, setBusy] = useState(false);
  const deckRef = useRef(null);
  const [startupId, setStartupId] = useState(null);
  const [vetting, setVetting] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get('/founder/journey');
      setJ(data);
      if (data.steps.find((s) => s.id === 'profile')?.status === 'completed') {
        const { data: lst } = await api.get('/startups/');
        if (lst.length) setStartupId(lst[0].startup_id);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load journey');
    }
    // Also fetch the founder's AI council vetting status (separate concern
    // — applies even before they have a startups[] row).
    try {
      const { data } = await api.get('/vetting/me/status');
      setVetting(data);
    } catch {/* silent */}
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 8000); // poll while AI council runs
    return () => clearInterval(t);
  }, []);

  const downloadTemplate = async () => {
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      const r = await fetch(`${base}/api/founder/pitch-deck-template.pdf`, {
        credentials: 'include',
      });
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'Vigilant_Pitch_Deck_Template.pdf';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Template downloaded — build your deck following this structure.');
    } catch { toast.error('Could not download template.'); }
  };

  const uploadDeck = async (f) => {
    if (!f || !startupId) {
      if (!startupId) toast.error('Create your startup first (step 1).');
      return;
    }
    if (f.size > 50 * 1024 * 1024) { toast.error('Max 50MB.'); return; }
    const fd = new FormData();
    fd.append('file', f);
    fd.append('kind', 'pitch_deck');
    fd.append('startup_id', startupId);
    setBusy(true);
    try {
      await api.post('/documents/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Pitch deck uploaded.');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed.');
    } finally { setBusy(false); }
  };

  if (!j) return <div className="text-sm text-slate-500">Loading your journey…</div>;

  return (
    <div>
      <div>
        <div className="ve-eyebrow">Founder Journey</div>
        <h1 className="display text-3xl md:text-4xl mt-2 font-bold">
          From founder → funded.<br/>
          <span className="ve-gold-text">Six rigorous steps.</span>
        </h1>
        <p className="text-sm text-slate-600 mt-3 max-w-2xl">
          Every founder follows the same sequence. The platform won&apos;t let you sign an NDA or MOU until earlier steps are complete — this protects you, us, and the integrity of the relationship.
        </p>
      </div>

      <VettingBanner v={vetting}/>

      {/* Progress ring */}
      <div className="mt-8 ve-card p-6 flex items-center gap-6">
        <div className="relative h-20 w-20 shrink-0">
          <svg className="absolute inset-0" viewBox="0 0 80 80">
            <circle cx="40" cy="40" r="34" fill="none" stroke="#E5E7EB" strokeWidth="6"/>
            <circle cx="40" cy="40" r="34" fill="none" stroke="#8B3FC5" strokeWidth="6"
                    strokeDasharray={`${(j.percent / 100) * 213.6} 213.6`}
                    strokeLinecap="round" transform="rotate(-90 40 40)"/>
          </svg>
          <div className="absolute inset-0 flex items-center justify-center font-bold display text-lg">
            {j.percent}%
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">
            {j.completed} of {j.total} steps completed
          </div>
          <div className="display text-xl font-bold mt-1">
            {j.next_step ? `Next: ${(j.steps.find((s) => s.id === j.next_step) || {}).label}` : 'All steps complete!'}
          </div>
        </div>
      </div>

      {/* Step cards */}
      <div className="mt-6 space-y-3">
        {j.steps.map((s, i) => {
          const Icon = STEP_ICONS[s.id] || Circle;
          const locked = s.status === 'locked';
          const done = s.status === 'completed';
          return (
            <div
              key={s.id}
              data-testid={`fjourney-step-${s.id}`}
              className={`ve-card p-5 flex items-start gap-4 ${locked ? 'opacity-60' : ''}`}
            >
              <span className={`h-10 w-10 rounded-md flex items-center justify-center shrink-0 ${
                done ? 'bg-emerald-50 text-emerald-700' :
                  locked ? 'bg-slate-50 text-slate-400' : 'bg-[#FFF8E5] text-[#5E1F8B]'
              }`}>
                {locked ? <Lock size={16}/> : done ? <CheckCircle2 size={18}/> : <Icon size={18}/>}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3">
                  <span className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-bold">Step {i + 1}</span>
                  <StatusPill status={s.status}/>
                </div>
                <h3 className="display text-lg font-bold mt-1">{s.label}</h3>
                <p className="text-xs text-slate-600 mt-1">{s.desc}</p>

                {/* Step-specific actions */}
                {s.id === 'profile' && s.status !== 'locked' && (
                  <Link to="/dashboard/startups" className="mt-3 ve-btn-secondary text-xs inline-flex">
                    {done ? 'Manage startup' : 'Create your startup'} →
                  </Link>
                )}

                {s.id === 'pitch_deck' && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button onClick={downloadTemplate} data-testid="fjourney-download-template"
                            className="ve-btn-ghost text-xs">
                      <Download size={12}/> Download template
                    </button>
                    {s.status !== 'locked' && (
                      <>
                        <button onClick={() => deckRef.current?.click()}
                                disabled={busy} data-testid="fjourney-upload-deck"
                                className="ve-btn-secondary text-xs">
                          <Upload size={12}/> {busy ? 'Uploading…' : done ? 'Replace deck' : 'Upload deck'}
                        </button>
                        <input ref={deckRef} type="file" accept=".pdf,.pptx,.ppt"
                               className="hidden" onChange={(e) => uploadDeck(e.target.files?.[0])}/>
                      </>
                    )}
                    {done && s.filename && (
                      <span className="text-xs text-emerald-700 self-center">✓ {s.filename}</span>
                    )}
                  </div>
                )}

                {s.id === 'ai_vetting' && (
                  <AIVettingActions
                    step={s}
                    startupId={startupId}
                    onRestart={async () => {
                      if (!startupId) return;
                      try {
                        await api.post('/startups/qa/restart', { startup_id: startupId });
                        toast.success('Fresh AI Q&A session unlocked — head to your startup page.');
                        load();
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || 'Could not restart');
                      }
                    }}
                  />
                )}

                {s.id === 'psychometric' && s.status !== 'locked' && (
                  <Link to={`/dashboard/startups${startupId ? `/${startupId}?tab=psycho` : ''}`}
                        data-testid="fjourney-go-psycho"
                        className="mt-3 ve-btn-secondary text-xs inline-flex">
                    {done ? `Leadership index: ${s.leadership_index || '—'}` : 'Take psychometric'} →
                  </Link>
                )}

                {(s.id === 'nda_ready' || s.id === 'mou_ready') && (
                  <Link to="/dashboard/agreements"
                        className={`mt-3 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.1em] ${
                          s.status === 'locked' ? 'text-slate-400 pointer-events-none' :
                            done ? 'text-emerald-700' : 'text-[#5E1F8B] hover:text-[#0A0A0A]'
                        }`}>
                    {locked ? '🔒 Complete earlier steps to unlock' : done ? '✓ Signed' : 'Open Agreements →'}
                  </Link>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-8 ve-card p-6 border-[#8B3FC5] bg-gradient-to-r from-[#FFF8E5] to-white">
        <div className="ve-eyebrow">Stuck on a step?</div>
        <p className="text-sm text-slate-700 mt-2">
          Email <a href="mailto:founders@vigilantentities.com" className="text-[#5E1F8B] font-bold">founders@vigilantentities.com</a> or open a compliance chat. The investment team responds within 1 working day.
        </p>
      </div>
    </div>
  );
}

function AIVettingActions({ step, startupId, onRestart }) {
  if (step.status === 'locked') return null;
  if (step.status === 'completed') {
    return (
      <div className="mt-3 flex items-center gap-3 flex-wrap">
        <span className="ve-eyebrow text-emerald-700">
          Passed · {step.final_score ?? '—'}/100 ({step.final_verdict ?? '—'})
        </span>
        {step.qa_override_by && (
          <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-700">
            · admin override
          </span>
        )}
      </div>
    );
  }

  if (step.status === 'blocked') {
    const locked = step.qa_locked;
    return (
      <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 p-3 dark:bg-rose-500/[0.08] dark:border-rose-500/30">
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-rose-700 dark:text-rose-300">
          AI Q&amp;A did not pass · {step.final_verdict ?? '—'} · {step.final_score ?? '—'}/100
        </div>
        <p className="text-xs text-rose-800 dark:text-rose-200 mt-1.5 leading-relaxed">
          The strict gate requires verdict <b>approve</b> and score <b>≥ 65</b>. Until you pass,
          your journey cannot advance to psychometric, NDA or MOU.
        </p>
        <p className="text-[11px] text-rose-700/80 dark:text-rose-300/80 mt-1.5">
          Attempts used: <b>{step.qa_attempts ?? 0}</b> · remaining: <b>{step.qa_attempts_left ?? 0}</b>
        </p>
        <div className="mt-2.5 flex flex-wrap gap-2">
          {!locked ? (
            <button onClick={onRestart} data-testid="fjourney-qa-retry"
              className="ve-btn-primary text-xs">
              Restart AI Q&A
            </button>
          ) : (
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-rose-700 dark:text-rose-300">
              All attempts used · awaiting human-reviewer override
            </div>
          )}
          <a href="mailto:founders@vigilantentities.com?subject=AI%20Q%26A%20override%20request"
            className="ve-btn-ghost text-xs">
            Contact reviewer
          </a>
        </div>
      </div>
    );
  }

  // status === 'open'
  return (
    <Link to={`/dashboard/startups${startupId ? `/${startupId}?tab=qa` : ''}`}
      data-testid="fjourney-go-aiqa"
      className="mt-3 ve-btn-secondary text-xs inline-flex">
      {step.qa_attempts > 0 ? `Restart AI Q&A · attempt ${(step.qa_attempts ?? 0) + 1}` : 'Start AI Q&A'} →
    </Link>
  );
}

function VettingBanner({ v }) {
  if (!v?.has_application) return null;
  const a = v.assessment || {};
  const status = a.status;
  const map = {
    ai_running: {
      icon: Clock, tone: 'sky',
      title: 'AI council deliberating…',
      msg: 'Three personas are scoring your pitch in parallel. This usually takes under a minute.',
    },
    awaiting_human: {
      icon: Sparkles, tone: 'amber',
      title: `AI verdict: ${(a.ai_verdict || '—').toUpperCase()}${a.ai_score != null ? ` · ${a.ai_score}/100` : ''}`,
      msg: 'A senior reviewer will email you with the final decision shortly.',
    },
    approved: {
      icon: CheckCircle2, tone: 'emerald',
      title: 'You are approved — welcome aboard.',
      msg: a.human_note || 'Continue your founder journey below to unlock NDA + MOU.',
    },
    improve: {
      icon: Sparkles, tone: 'amber',
      title: 'Improvement program',
      msg: a.human_note || 'Our advisors will help you sharpen your pitch before the next attempt.',
    },
    meeting: {
      icon: MessageCircle, tone: 'amber',
      title: 'Meeting requested',
      msg: a.human_note || 'A reviewer would like to discuss your venture live before deciding.',
    },
    rejected: {
      icon: XCircle, tone: 'rose',
      title: 'Application declined',
      msg: a.human_note || 'Thank you for applying. You are welcome to re-apply as the venture evolves.',
    },
  };
  const cfg = map[status];
  if (!cfg) return null;
  const Icon = cfg.icon;
  const toneCls = {
    sky:     'border-sky-200 bg-sky-50 text-sky-800 dark:bg-sky-500/[0.08] dark:text-sky-200',
    amber:   'border-amber-200 bg-amber-50 text-amber-800 dark:bg-amber-500/[0.08] dark:text-amber-200',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:bg-emerald-500/[0.08] dark:text-emerald-200',
    rose:    'border-rose-200 bg-rose-50 text-rose-800 dark:bg-rose-500/[0.08] dark:text-rose-200',
  }[cfg.tone];
  return (
    <div data-testid="founder-vetting-banner" className={`mt-6 ve-card p-5 flex items-start gap-4 ${toneCls}`}>
      <Icon className="mt-0.5 flex-shrink-0" size={22}/>
      <div className="flex-1">
        <div className="text-xs uppercase tracking-[0.2em] font-bold">{cfg.title}</div>
        <div className="text-sm mt-1.5 leading-relaxed">{cfg.msg}</div>
        {a.ai_rationale && status === 'awaiting_human' && (
          <p className="text-xs italic opacity-80 mt-2">{a.ai_rationale}</p>
        )}
      </div>
    </div>
  );
}
